import { Component } from '@angular/core';
import { captureMessage } from '@sentry/angular';
import { concat, Observable, ObservedValueOf, of, race, ReplaySubject, Subject, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, map, scan, share, switchMap, tap } from 'rxjs/operators';
import { LiveUpdateService } from '../services/live-update.service';

@Component({
	selector: 'app-offline-bar',
	templateUrl: './offline-bar.component.html',
	styleUrls: ['./offline-bar.component.scss'],
})
export class OfflineBarComponent {
	isDisplaying$: Observable<boolean>;

	private userClose$ = new Subject();

	status$: Observable<{
		icon: string;
		text: string;
		action: 'close' | 'reconnect';
		actionIcon: string;
	}>;

	constructor(private liveUpdateService: LiveUpdateService) {
		const connectionWithCount$ = this.liveUpdateService.isConnected$.pipe(
			// changes in the connection within 5 seconds will be ignored. This prevents the
			// offline bar from being too sensitive to connection status. For example, if
			// `isConnected$` has these state changes over the span of 5 seconds:
			// [true, false, true], only a single true would emit. If the delay between
			// false and true is more than 5 seconds a false will emit then a true will emit.
			debounceTime(5_000),
			distinctUntilChanged(),
			scan<boolean, [number, boolean]>(
				([changeCount, _], isConnected) => {
					return [changeCount + 1, isConnected];
				},
				[0, false]
			),
			share({
				connector: () => new ReplaySubject(1),
				resetOnRefCountZero: true,
				resetOnComplete: true,
				resetOnError: true,
			})
		);

		this.isDisplaying$ = connectionWithCount$.pipe(
			switchMap(([changeCount, isConnected]) => {
				const isFirstStatus = changeCount === 1;
				if (isConnected && !isFirstStatus) {
					return concat(
						of(true),
						// close after timer or when user clicks
						race(timer(10_000), this.userClose$.pipe(first())).pipe(map(() => false))
					);
				} else if (!isConnected) {
					return of(true);
				} else {
					return of(false);
				}
			}),
			distinctUntilChanged(),
			tap((isDisplaying) => {
				if (isDisplaying) {
					captureMessage('Displaying offline bar');
				}
			})
		);

		this.status$ = connectionWithCount$.pipe(
			map(([_, isConnected]) => {
				if (isConnected) {
					return {
						icon: './assets/Connected (Jade).svg',
						text: 'Your internet connection was restored.',
						action: 'close',
						actionIcon: './assets/Cancel (Blue-Gray).svg',
					};
				} else {
					return {
						icon: './assets/Disconnected (Blue-Gray).svg',
						text: 'You are currently offline.',
						action: 'reconnect',
						actionIcon: './assets/Refresh (Jade).svg',
					};
				}
			})
		);
	}

	action(action: ObservedValueOf<typeof this.status$>['action']) {
		switch (action) {
			case 'close': {
				this.userClose$.next(undefined);
				break;
			}
			case 'reconnect': {
				this.liveUpdateService.forceReconnect();
				break;
			}
			default: {
				const exhaustiveCheck: never = action;
				// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				throw new Error(`Unhandled action case: ${exhaustiveCheck}`);
			}
		}
	}
}
